import React, { useState, useEffect } from 'react';
import './CompanyManagement.css';

function CompanyManagement({ user }) {
  const [myCompany, setMyCompany] = useState(null);
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showAddCompanyForm, setShowAddCompanyForm] = useState(false);

  // DEBUG: Log user prop
  useEffect(() => {
    console.log('CompanyManagement - User prop:', user);
  }, [user]);
  
  // Add company form state
  const [newCompany, setNewCompany] = useState({
    name: '',
    short_code: '',
    id_field_label: 'Network ID',
    website_url: ''
  });

  useEffect(() => {
    fetchMyCompany();
    fetchAllCompanies();
  }, []);

  const fetchMyCompany = async () => {
    try {
      const response = await fetch('/backend/api/admin/set-my-company.php', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.company) {
          setMyCompany(data.company);
          setSelectedCompanyId(data.company.id);
        }
      }
    } catch (error) {
      console.error('Error fetching company:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCompanies = async () => {
    try {
      const response = await fetch('/backend/api/admin/network-companies.php', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAllCompanies(data.companies);
        }
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    
    if (!selectedCompanyId) {
      setMessage({ type: 'error', text: 'Please select a company' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch('/backend/api/admin/set-my-company.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          network_company_id: parseInt(selectedCompanyId)
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMessage({ type: 'success', text: `Company set to ${data.company_name}` });
          fetchMyCompany();
        } else {
          setMessage({ type: 'error', text: data.error || 'Failed to save company' });
        }
      } else {
        setMessage({ type: 'error', text: 'Server error occurred' });
      }
    } catch (error) {
      console.error('Error saving company:', error);
      setMessage({ type: 'error', text: 'Failed to save company' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    
    if (!newCompany.name.trim()) {
      setMessage({ type: 'error', text: 'Company name is required' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      console.log('Adding company:', newCompany);
      
      const response = await fetch('/backend/api/admin/network-companies.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newCompany)
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Response data:', data);
        
        if (data.success) {
          setMessage({ type: 'success', text: 'Company added successfully' });
          setShowAddCompanyForm(false);
          setNewCompany({
            name: '',
            short_code: '',
            id_field_label: 'Network ID',
            website_url: ''
          });
          fetchAllCompanies();
        } else {
          setMessage({ type: 'error', text: data.error || 'Failed to add company' });
        }
      } else {
        const errorText = await response.text();
        console.error('Server error:', response.status, errorText);
        
        // Try to parse error as JSON to see debug info
        try {
          const errorData = JSON.parse(errorText);
          console.error('Error debug data:', errorData);
          setMessage({ type: 'error', text: `Server error (${response.status}): ${errorData.error}` });
        } catch (e) {
          setMessage({ type: 'error', text: `Server error (${response.status}): ${errorText.substring(0, 100)}` });
        }
      }
    } catch (error) {
      console.error('Error adding company:', error);
      setMessage({ type: 'error', text: `Network error: ${error.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading company settings...</div>;
  }

  return (
    <div className="company-management">
      <div className="section-header">
        <h2>Network Company Management</h2>
        <p>Configure your network marketing company settings. All contacts you promote to marketer will automatically inherit your company.</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Current Company Section */}
      <div className="current-company-section">
        <h3>Your Company</h3>
        {myCompany ? (
          <div className="company-display">
            <div className="company-info-grid">
              <div className="info-item">
                <strong>Company Name:</strong>
                <span>{myCompany.name}</span>
              </div>
              <div className="info-item">
                <strong>Short Code:</strong>
                <span>{myCompany.short_code || 'Not set'}</span>
              </div>
              <div className="info-item">
                <strong>Member ID Label:</strong>
                <span>{myCompany.id_field_label}</span>
              </div>
              <div className="info-item">
                <strong>Website:</strong>
                <span>{myCompany.website_url || 'Not set'}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="no-company-message">
            <p>⚠️ You haven't selected a company yet. Please select one below to enable marketer promotions.</p>
          </div>
        )}
      </div>

      {/* Select Company Form */}
      <div className="select-company-section">
        <h3>Select Your Company</h3>
        <form onSubmit={handleSaveCompany} className="company-form">
          <div className="form-group">
            <label htmlFor="company-select">Choose Company:</label>
            <select
              id="company-select"
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              required
            >
              <option value="">Select a company...</option>
              {allCompanies
                .filter(c => c.is_active)
                .map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name} {company.short_code ? `(${company.short_code})` : ''}
                  </option>
                ))}
            </select>
          </div>

          <button type="submit" className="save-btn" disabled={saving || !selectedCompanyId}>
            {saving ? 'Saving...' : 'Save Company'}
          </button>
        </form>
      </div>

      {/* Add New Company Section */}
      <div className="add-company-section">
        <div className="section-header-with-btn">
          <h3>Manage Companies</h3>
          <button 
            className="add-company-btn"
            onClick={() => setShowAddCompanyForm(!showAddCompanyForm)}
          >
            {showAddCompanyForm ? '✖ Cancel' : '+ Add New Company'}
          </button>
        </div>

        {showAddCompanyForm && (
          <form onSubmit={handleAddCompany} className="add-company-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="company-name">Company Name *</label>
                <input
                  type="text"
                  id="company-name"
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                  placeholder="e.g., Leaders Master Mind"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="short-code">Short Code</label>
                <input
                  type="text"
                  id="short-code"
                  value={newCompany.short_code}
                  onChange={(e) => setNewCompany({...newCompany, short_code: e.target.value})}
                  placeholder="e.g., LMM"
                  maxLength="50"
                />
              </div>

              <div className="form-group">
                <label htmlFor="id-label">Member ID Label</label>
                <input
                  type="text"
                  id="id-label"
                  value={newCompany.id_field_label}
                  onChange={(e) => setNewCompany({...newCompany, id_field_label: e.target.value})}
                  placeholder="e.g., Network ID, Distributor ID"
                />
              </div>

              <div className="form-group">
                <label htmlFor="website">Website URL</label>
                <input
                  type="url"
                  id="website"
                  value={newCompany.website_url}
                  onChange={(e) => setNewCompany({...newCompany, website_url: e.target.value})}
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Adding...' : 'Add Company'}
            </button>
          </form>
        )}

        {/* List of all companies */}
        <div className="companies-list">
          <h4>Existing Companies</h4>
          <div className="companies-grid">
            {allCompanies.map(company => (
              <div key={company.id} className={`company-card ${!company.is_active ? 'inactive' : ''}`}>
                <div className="company-card-header">
                  <h5>{company.name}</h5>
                  {!company.is_active && <span className="inactive-badge">Inactive</span>}
                </div>
                <div className="company-card-details">
                  {company.short_code && <p><strong>Code:</strong> {company.short_code}</p>}
                  <p><strong>ID Label:</strong> {company.id_field_label}</p>
                  {company.website_url && <p><strong>Website:</strong> <a href={company.website_url} target="_blank" rel="noopener noreferrer">{company.website_url}</a></p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyManagement;
